import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { 
  AlertTriangle, 
  Trash2, 
  XCircle, 
  RefreshCw, 
  HelpCircle,
  Loader2,
  Shield,
  FileX
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DELETION_REASONS = [
  {
    value: 'GOVT_CANCELLED',
    label: 'Government Cancelled',
    description: 'The solicitation was cancelled by the government',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  {
    value: 'INTERNAL_PIVOT',
    label: 'Internal Pivot / No-Go',
    description: 'Strategic decision to not pursue this opportunity',
    icon: RefreshCw,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  {
    value: 'START_OVER',
    label: 'Start Over (Reset All Data)',
    description: 'Clear all documents and analysis, keep intake metadata',
    icon: FileX,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  {
    value: 'OTHER',
    label: 'Other',
    description: 'Provide your own reason below',
    icon: HelpCircle,
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200'
  }
];

export default function DeletePursuitModal({ 
  isOpen, 
  onClose, 
  pursuit, 
  onDeleted 
}) {
  const { getAuthHeaders } = useAuth();
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const selectedReasonConfig = DELETION_REASONS.find(r => r.value === selectedReason);
  const isStartOver = selectedReason === 'START_OVER';
  const requiresConfirmation = !isStartOver; // Start Over doesn't need "DELETE" confirmation
  const canDelete = selectedReason && 
    (selectedReason !== 'OTHER' || notes.trim()) &&
    (!requiresConfirmation || confirmText === 'DELETE');

  const handleDelete = async () => {
    if (!canDelete) return;
    
    setIsDeleting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/purge/pursuit/${pursuit.id}/delete`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: selectedReason,
          notes: notes.trim() || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete pursuit');
      }

      const data = await response.json();
      
      if (isStartOver) {
        toast.success('Pursuit reset successfully', {
          description: `${data.documents_purged} documents cleared. Intake metadata preserved.`
        });
      } else {
        toast.success('Pursuit decommissioned', {
          description: `Will be permanently deleted on ${new Date(data.scheduled_permanent_deletion).toLocaleDateString()}`
        });
      }
      
      onDeleted?.(data);
      onClose();
      
    } catch (err) {
      console.error('Delete pursuit error:', err);
      toast.error('Failed to delete pursuit', {
        description: err.message
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setNotes('');
    setConfirmText('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="delete-pursuit-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-slate-900">
                Delete Pursuit
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                {pursuit?.opportunity_name}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Reason Selection */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-3 block">
              Reason for Deletion
            </Label>
            <RadioGroup 
              value={selectedReason} 
              onValueChange={setSelectedReason}
              className="space-y-2"
            >
              {DELETION_REASONS.map((reason) => {
                const Icon = reason.icon;
                const isSelected = selectedReason === reason.value;
                
                return (
                  <div 
                    key={reason.value}
                    className={`
                      flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all
                      ${isSelected ? `${reason.bgColor} ${reason.borderColor}` : 'bg-white border-slate-200 hover:border-slate-300'}
                    `}
                    onClick={() => setSelectedReason(reason.value)}
                    data-testid={`delete-reason-${reason.value.toLowerCase()}`}
                  >
                    <RadioGroupItem 
                      value={reason.value} 
                      id={reason.value}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <Label 
                        htmlFor={reason.value} 
                        className={`text-sm font-medium cursor-pointer flex items-center gap-2 ${isSelected ? reason.color : 'text-slate-900'}`}
                      >
                        <Icon className="w-4 h-4" />
                        {reason.label}
                      </Label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {reason.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {/* Notes (required for "Other") */}
          {selectedReason && (
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Additional Notes {selectedReason === 'OTHER' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter any additional details for the audit trail..."
                className="min-h-[80px]"
                data-testid="delete-notes-input"
              />
            </div>
          )}

          {/* Start Over Warning */}
          {isStartOver && (
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex gap-2">
                <Shield className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-purple-800">What "Start Over" does:</p>
                  <ul className="text-purple-700 mt-1 space-y-1">
                    <li>- Clears all uploaded documents and analysis</li>
                    <li>- Resets Heuristic Fit Score to 0</li>
                    <li>- <strong>Preserves</strong> intake metadata (Agency, RFP#, Title)</li>
                    <li>- <strong>Preserves</strong> any GOLD SOURCE documents</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Soft Delete Warning */}
          {selectedReason && !isStartOver && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">30-Day Recovery Window</p>
                  <p className="text-amber-700 mt-1">
                    This pursuit will be moved to "Deleted" status and held for 30 days 
                    before permanent deletion. You can restore it during this period.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Input (for actual deletion, not Start Over) */}
          {selectedReason && !isStartOver && (
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Type <span className="font-mono bg-slate-100 px-1 rounded">DELETE</span> to confirm
              </Label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
                data-testid="delete-confirm-input"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={!canDelete || isDeleting}
              className={`flex-1 ${isStartOver ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
              data-testid="delete-confirm-btn"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isStartOver ? 'Resetting...' : 'Deleting...'}
                </>
              ) : (
                <>
                  {isStartOver ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset Pursuit
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Pursuit
                    </>
                  )}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
